import React, { FC, ReactNode, useRef } from 'react';
import Box, { BoxProps } from '@mui/material/Box';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';

const MOUNT_TRANSITION_DURATION = 500;
const UNMOUNT_TRANSITION_DURATION = 0;

interface Props {
  show: boolean;
  children: ReactNode;
  mountDuration?: number;
  unmountDuration?: number;
  transitionStyles?: Partial<Record<TransitionStatus, { opacity: number }>>;
  className?: string;
  sx?: BoxProps['sx'];
  mountOnEnter?: boolean;
  unmountOnExit?: boolean;
}

export const ComponentWithFade: FC<Props> = ({
  show,
  mountDuration = MOUNT_TRANSITION_DURATION,
  unmountDuration = UNMOUNT_TRANSITION_DURATION,
  transitionStyles: transitionStylesProp,
  children,
  mountOnEnter = true,
  unmountOnExit = true,
  className,
  sx = {},
}) => {
  const nodeRef = useRef(null);
  const duration = show ? mountDuration : unmountDuration;

  const defaultStyle = {
    transition: `opacity ${duration}ms ease-in-out`,
    opacity: 0,
  };

  const transitionStyles: Record<TransitionStatus, { opacity: number }> = {
    entering: { opacity: 0.5 },
    entered: { opacity: 1 },
    exiting: { opacity: 0.5 },
    exited: { opacity: 0 },
    unmounted: { opacity: 0 },
    ...transitionStylesProp,
  };

  return (
    <Transition
      nodeRef={nodeRef}
      appear={show}
      in={show}
      timeout={duration}
      mountOnEnter={mountOnEnter}
      unmountOnExit={unmountOnExit}
    >
      {(state) => {
        return (
          <Box sx={{ height: '100%', ...defaultStyle, ...transitionStyles[state], ...sx }} className={className}>
            {children}
          </Box>
        );
      }}
    </Transition>
  );
};
